"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ArrowRight, BookOpen, ChevronRight, CircleCheck as CheckCircle2, Cpu, Layers, Search, SlidersHorizontal as Sliders, TrendingUp } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const keyTopics = [{
  title: "Process Analytical Technology",
  description: "Real-time critical quality attribute (CQA) monitoring and closed-loop process control.",
  icon: Activity,
  link: "/ProcessAnalyticalTechnologyPage",
  tag: "Core Framework"
}, {
  title: "NIR & Raman Spectroscopy",
  description: "In-line probe selection, non-destructive molecular identification, and interface engineering.",
  icon: Cpu,
  link: "/NirProcessSpectrometersPage",
  tag: "Instrumentation"
}, {
  title: "Chemometrics & Modeling",
  description: "Calibration transfer, PLS model development, and validation under ICH Q2/Q14 guidelines.",
  icon: Sliders,
  link: "/ChemometricModelDevelopmentPage",
  tag: "Data Science"
}, {
  title: "Powder Characterisation",
  description: "Rheology, bulk density, shear testing, and flowability metrics for solid dosage forms.",
  icon: Layers,
  link: "/PowderTestingPage",
  tag: "Physical Testing"
}];
const highlights = ["ICH Q8-Q13 regulatory alignment guides", "Real-time blend uniformity & drying endpoints", "Standardized qualification & lifecycle protocols"];
export default function KnowledgeCentre() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="knowledgecentrehero" data-nav="light" className="relative w-full overflow-hidden bg-background py-16 md:py-24 border-b border-border">
      {/* Decorative background grid subtle overlay */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] [background-image:radial-gradient(#000_1px,transparent_1px)] [background-size:24px_24px] dark:[background-image:radial-gradient(#fff_1px,transparent_1px)]" />

      <div className="page-container relative">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-8">
          <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground sm:text-sm">
            <li className="inline-flex items-center gap-1.5">
              <Link to="/HomePage" className="transition-colors hover:text-foreground">
                Home
              </Link>
            </li>
            <li aria-hidden="true" className="text-muted-foreground/60">
              <ChevronRight className="size-3.5" />
            </li>
            <li className="inline-flex items-center gap-1.5">
              <Link to="/ResourcesPage" className="transition-colors hover:text-foreground">
                Resources
              </Link>
            </li>
            <li aria-hidden="true" className="text-muted-foreground/60">
              <ChevronRight className="size-3.5" />
            </li>
            <li className="inline-flex items-center gap-1.5">
              <span aria-current="page" className="font-medium text-foreground">
                PAT Knowledge Centre
              </span>
            </li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          {/* Main Hero Copy */}
          <div className="lg:col-span-7">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="flex flex-col items-start">
              <Badge variant="secondary" className="mb-4 gap-1.5 px-3 py-1 text-xs font-semibold">
                <BookOpen className="size-3.5 text-primary" />
                PAT Knowledge Centre
              </Badge>

              <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:leading-[1.15]">
                Technical guidance for in-line analytical science and process control
              </h1>

              <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
                Explore foundational frameworks, measurement methods, and chemometric validation
                workflows for regulated pharmaceutical, chemical, and biomanufacturing environments.
              </p>

              {/* Core Feature Bullet Points */}
              <div className="mt-6 flex flex-col gap-2.5">
                {highlights.map((item, i) => <div key={i} data-index={i} className="flex items-center gap-2 text-sm text-foreground">
                    <CheckCircle2 className="size-4 shrink-0 text-primary" />
                    <span>{item}</span>
                  </div>)}
              </div>

              {/* Action Buttons */}
              <div className="mt-8 flex flex-wrap items-center gap-3.5">
                <Button asChild size="lg" className="rounded-md">
                  <Link to="/ApplicationConsultationPage" className="inline-flex items-center gap-2">
                    <span>Discuss Your Application</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="rounded-md">
                  <Link to="/SolutionsPage" className="inline-flex items-center gap-2">
                    <Search className="size-4 text-muted-foreground" />
                    <span>Browse Solutions</span>
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>

          {/* Overview Stat / Visual Card */}
          <div className="lg:col-span-5">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            scale: 1
          } : {
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.1,
            ease: "easeOut"
          }}>
              <div className="relative rounded-2xl border border-border bg-card p-6 shadow-md md:p-8">
                <div className="flex items-center justify-between pb-4 border-b border-border">
                  <div className="flex items-center gap-2.5">
                    <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <TrendingUp className="size-5" />
                    </div>
                    <div>
                      <h2 className="text-sm font-semibold text-card-foreground">Domain Architecture</h2>
                      <p className="text-xs text-muted-foreground">Regulated Process Systems</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">QbD Compliant</Badge>
                </div>

                <div className="mt-5 space-y-4">
                  <div className="rounded-lg bg-secondary/70 p-3.5">
                    <div className="text-xs font-medium text-muted-foreground">Quality by Design (QbD)</div>
                    <div className="mt-1 text-sm font-semibold text-secondary-foreground">
                      Continuous verification over conventional batch testing
                    </div>
                  </div>

                  <div className="rounded-lg bg-secondary/70 p-3.5">
                    <div className="text-xs font-medium text-muted-foreground">Measurement Scope</div>
                    <div className="mt-1 text-sm font-semibold text-secondary-foreground">
                      In-line NIR probes, Raman touch-free analyzers, and automated powder testers
                    </div>
                  </div>

                  <div className="rounded-lg bg-secondary/70 p-3.5">
                    <div className="text-xs font-medium text-muted-foreground">Target Outcome</div>
                    <div className="mt-1 text-sm font-semibold text-secondary-foreground">
                      Reduced cycle times, robust batch uniformity, and zero waste
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between pt-4 border-t border-border text-xs text-muted-foreground">
                  <span>Standard Reference Guides</span>
                  <Link to="/ResourcesPage" className="font-semibold text-primary hover:underline">
                    View Resource Library &rarr;
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Key Knowledge Domain Topic Cards */}
        <div className="mt-16 pt-12 border-t border-border">
          <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">Core Disciplines</p>
              <h2 className="mt-1 font-default text-xl font-bold text-foreground sm:text-2xl">
                Select a topic to explore technical protocols
              </h2>
            </div>
            <Button asChild variant="ghost" size="sm" className="w-fit text-xs font-medium">
              <Link to="/ServicesPage" className="inline-flex items-center gap-1.5">
                <span>View engineering services</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {keyTopics.map((topic, i) => {
            const IconComponent = topic.icon;
            return <div key={topic.title} data-index={i}>
                  <Card className="h-full border border-border bg-card transition-[box-shadow,border-color] duration-200 hover:border-primary/50 hover:shadow-md">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <IconComponent className="size-5" />
                        </div>
                        <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                          {topic.tag}
                        </span>
                      </div>
                      <CardTitle className="mt-3 font-default text-base font-semibold text-card-foreground">
                        {topic.title}
                      </CardTitle>
                      <CardDescription className="text-xs leading-relaxed text-muted-foreground">
                        {topic.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="mt-auto pt-2">
                      <Button asChild variant="link" size="sm" className="h-auto p-0 text-xs font-semibold">
                        <Link to={topic.link} className="inline-flex items-center gap-1">
                          <span>Read methodology</span>
                          <ArrowRight className="size-3" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}